import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const POLICY_TYPES = [
  { value: "FULFILLMENT", label: "Postage / Fulfillment" },
  { value: "PAYMENT", label: "Payment" },
  { value: "RETURN", label: "Return" },
];

export default function ListTemplatesManager() {
  const [rows, setRows] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [policiesByType, setPoliciesByType] = useState({
    FULFILLMENT: [],
    PAYMENT: [],
    RETURN: [],
  });

  const [open, setOpen] = useState(false);
  const [openTier, setOpenTier] = useState(false);
  const [openPolicies, setOpenPolicies] = useState(false);

  const [editTemplateId, setEditTemplateId] = useState(null);
  const [editTierId, setEditTierId] = useState(null);

  const [form, setForm] = useState({
    name: "",
    fulfillment_policy_id: "",
    payment_policy_id: "",
    return_policy_id: "",
    is_default: true,
  });

  const [tierForm, setTierForm] = useState({
    name: "",
    min_price: "",
    max_price: "",
    fulfillment_policy_id: "",
    payment_policy_id: "",
    return_policy_id: "",
    description_prefix: "",
    description_suffix: "",
    is_default: true,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [tRes, tierRes] = await Promise.all([
        axios.get("/api/list_templates"),
        axios.get("/api/list_template_tiers"),
      ]);
      setRows(tRes?.data?.templates || []);
      setTiers(tierRes?.data?.tiers || []);
    } finally {
      setLoading(false);
    }
  };

  const refreshPolicies = async () => {
    try {
      const [f, p, r] = await Promise.all([
        axios.get("/api/list_templates/policies?type=FULFILLMENT"),
        axios.get("/api/list_templates/policies?type=PAYMENT"),
        axios.get("/api/list_templates/policies?type=RETURN"),
      ]);
      setPoliciesByType({
        FULFILLMENT: f?.data?.policies || [],
        PAYMENT: p?.data?.policies || [],
        RETURN: r?.data?.policies || [],
      });
    } catch (e) {
      // no-op; policies may not be downloaded yet
    }
  };

  useEffect(() => {
    refresh();
    refreshPolicies();
  }, []);

  const templateColumns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "name", headerName: "Name", width: 240 },
      { field: "fulfillment_policy_id", headerName: "Postage/Fulfillment Policy", width: 260 },
      { field: "payment_policy_id", headerName: "Payment Policy", width: 220 },
      { field: "return_policy_id", headerName: "Return Policy", width: 220 },
      {
        field: "is_default",
        headerName: "Default",
        width: 110,
        valueGetter: (p) => (p?.row?.is_default ? "Yes" : ""),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 320,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={!!p?.row?.is_default}
              onClick={async () => {
                await axios.post(`/api/list_templates/${p?.row?.id}/set_default`);
                await refresh();
              }}
            >
              SET DEFAULT
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setEditTemplateId(p?.row?.id);
                setForm({
                  name: p?.row?.name || "",
                  fulfillment_policy_id: p?.row?.fulfillment_policy_id || "",
                  payment_policy_id: p?.row?.payment_policy_id || "",
                  return_policy_id: p?.row?.return_policy_id || "",
                  is_default: !!p?.row?.is_default,
                });
                setOpen(true);
              }}
            >
              EDIT
            </Button>

            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={!!p?.row?.is_default}
              onClick={async () => {
                const ok = window.confirm(
                  `Delete template "${p?.row?.name}" (ID ${p?.row?.id})? This cannot be undone.`
                );
                if (!ok) return;
                try {
                  await axios.delete(`/api/list_templates/${p?.row?.id}`);
                  await refresh();
                } catch (e) {
                  alert(e?.response?.data?.error || e?.message || "Failed to delete template");
                }
              }}
            >
              DELETE
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  const tierColumns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "name", headerName: "Tier Name", width: 220 },
      { field: "min_price", headerName: "Min $", width: 110 },
      { field: "max_price", headerName: "Max $", width: 110 },
      { field: "fulfillment_policy_id", headerName: "Fulfillment", width: 220 },
      { field: "payment_policy_id", headerName: "Payment", width: 200 },
      { field: "return_policy_id", headerName: "Return", width: 200 },
      {
        field: "is_default",
        headerName: "Default",
        width: 110,
        valueGetter: (p) => (p?.row?.is_default ? "Yes" : ""),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 360,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={!!p?.row?.is_default}
              onClick={async () => {
                await axios.post(`/api/list_template_tiers/${p?.row?.id}/set_default`);
                await refresh();
              }}
            >
              SET DEFAULT
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setEditTierId(p?.row?.id);
                setTierForm({
                  name: p?.row?.name || "",
                  min_price: p?.row?.min_price ?? "",
                  max_price: p?.row?.max_price ?? "",
                  fulfillment_policy_id: p?.row?.fulfillment_policy_id || "",
                  payment_policy_id: p?.row?.payment_policy_id || "",
                  return_policy_id: p?.row?.return_policy_id || "",
                  description_prefix: p?.row?.description_prefix || "",
                  description_suffix: p?.row?.description_suffix || "",
                  is_default: !!p?.row?.is_default,
                });
                setOpenTier(true);
              }}
            >
              EDIT
            </Button>

            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={!!p?.row?.is_default}
              onClick={async () => {
                const ok = window.confirm(
                  `Delete tier "${p?.row?.name}" (ID ${p?.row?.id})? This cannot be undone.`
                );
                if (!ok) return;
                try {
                  await axios.delete(`/api/list_template_tiers/${p?.row?.id}`);
                  await refresh();
                } catch (e) {
                  alert(e?.response?.data?.error || e?.message || "Failed to delete tier");
                }
              }}
            >
              DELETE
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  const handleSaveTemplate = async () => {
    try {
      if (editTemplateId) {
        await axios.put(`/api/list_templates/${editTemplateId}`, form);
      } else {
        await axios.post("/api/list_templates", form);
      }
      setOpen(false);
      setEditTemplateId(null);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Failed to save template");
    }
  };

  const handleSaveTier = async () => {
    try {
      if (editTierId) {
        await axios.put(`/api/list_template_tiers/${editTierId}`, tierForm);
      } else {
        await axios.post("/api/list_template_tiers", tierForm);
      }
      setOpenTier(false);
      setEditTierId(null);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Failed to save tier");
    }
  };

  const handleDownloadPolicies = async () => {
    try {
      const res = await axios.post("/api/list_templates/policies/download");
      await refreshPolicies();
      alert(
        `Downloaded policies. Fulfillment: ${res?.data?.result?.downloaded?.FULFILLMENT || 0}, ` +
          `Payment: ${res?.data?.result?.downloaded?.PAYMENT || 0}, ` +
          `Return: ${res?.data?.result?.downloaded?.RETURN || 0}`
      );
    } catch (e) {
      alert(e?.response?.data?.error || e?.message || "Failed to download policies");
    }
  };

  const policyOptions = (type) => policiesByType?.[type] || [];

  const PolicySelect = ({ label, type, value, onChange }) => (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <MenuItem value="">
          <em>Select…</em>
        </MenuItem>
        {policyOptions(type).map((p) => (
          <MenuItem key={`${p.policy_type}-${p.policy_id}`} value={p.policy_id}>
            {p.name} ({p.policy_id})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">List Templates</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={handleDownloadPolicies}>
            Download Policies
          </Button>
          <Button variant="outlined" onClick={() => setOpenPolicies(true)}>
            View Policies
          </Button>
          <Button variant="contained" onClick={() => {
            setEditTemplateId(null);
            setForm({
              name: "",
              fulfillment_policy_id: "",
              payment_policy_id: "",
              return_policy_id: "",
              is_default: true,
            });
            setOpen(true);
          }}>
            New Template
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2">
        Templates are used by the Listings publish flow to select postage/fulfillment, payment, and return policies.
        Price Tiers can override the default template based on listing price and can add tracked/untracked text via
        description prefix/suffix.
      </Typography>

      <Typography variant="subtitle1">Templates (Legacy Default)</Typography>
      <DataGrid
        autoHeight
        rows={rows}
        columns={templateColumns}
        getRowId={(r) => r?.id}
        loading={loading}
        pageSize={25}
        rowsPerPageOptions={[25, 100]}
        disableRowSelectionOnClick
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1">Price Tiers</Typography>
        <Button variant="contained" onClick={() => {
            setEditTierId(null);
            setTierForm({
              name: "",
              min_price: "",
              max_price: "",
              fulfillment_policy_id: "",
              payment_policy_id: "",
              return_policy_id: "",
              description_prefix: "",
              description_suffix: "",
              is_default: true,
            });
            setOpenTier(true);
          }}>
          New Tier
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        rows={tiers}
        columns={tierColumns}
        getRowId={(r) => r?.id}
        loading={loading}
        pageSize={25}
        rowsPerPageOptions={[25, 100]}
        disableRowSelectionOnClick
      />

      {/* New Template Dialog */}
      <Dialog open={open} onClose={() => { setOpen(false); setEditTemplateId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editTemplateId ? "Edit List Template" : "New List Template"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
            />

            <PolicySelect
              label="Postage/Fulfillment Policy"
              type="FULFILLMENT"
              value={form.fulfillment_policy_id}
              onChange={(v) => setForm((p) => ({ ...p, fulfillment_policy_id: v }))}
            />
            <PolicySelect
              label="Payment Policy"
              type="PAYMENT"
              value={form.payment_policy_id}
              onChange={(v) => setForm((p) => ({ ...p, payment_policy_id: v }))}
            />
            <PolicySelect
              label="Return Policy"
              type="RETURN"
              value={form.return_policy_id}
              onChange={(v) => setForm((p) => ({ ...p, return_policy_id: v }))}
            />

            <Typography variant="caption" color="text.secondary">
              If you have not downloaded policies yet, you can paste IDs manually.
            </Typography>
            <TextField
              label="Fulfillment Policy ID (manual override)"
              value={form.fulfillment_policy_id}
              onChange={(e) => setForm((p) => ({ ...p, fulfillment_policy_id: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Payment Policy ID (manual override)"
              value={form.payment_policy_id}
              onChange={(e) => setForm((p) => ({ ...p, payment_policy_id: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Return Policy ID (manual override)"
              value={form.return_policy_id}
              onChange={(e) => setForm((p) => ({ ...p, return_policy_id: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); setEditTemplateId(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplate}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Tier Dialog */}
      <Dialog open={openTier} onClose={() => { setOpenTier(false); setEditTierId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editTierId ? "Edit Price Tier" : "New Price Tier"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tier Name"
              value={tierForm.name}
              onChange={(e) => setTierForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Min Price ($)"
                value={tierForm.min_price}
                onChange={(e) => setTierForm((p) => ({ ...p, min_price: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Max Price ($)"
                value={tierForm.max_price}
                onChange={(e) => setTierForm((p) => ({ ...p, max_price: e.target.value }))}
                fullWidth
              />
            </Stack>

            <PolicySelect
              label="Postage/Fulfillment Policy"
              type="FULFILLMENT"
              value={tierForm.fulfillment_policy_id}
              onChange={(v) => setTierForm((p) => ({ ...p, fulfillment_policy_id: v }))}
            />
            <PolicySelect
              label="Payment Policy"
              type="PAYMENT"
              value={tierForm.payment_policy_id}
              onChange={(v) => setTierForm((p) => ({ ...p, payment_policy_id: v }))}
            />
            <PolicySelect
              label="Return Policy"
              type="RETURN"
              value={tierForm.return_policy_id}
              onChange={(v) => setTierForm((p) => ({ ...p, return_policy_id: v }))}
            />

            <TextField
              label="Description Prefix (optional)"
              value={tierForm.description_prefix}
              onChange={(e) => setTierForm((p) => ({ ...p, description_prefix: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Description Suffix (optional)"
              value={tierForm.description_suffix}
              onChange={(e) => setTierForm((p) => ({ ...p, description_suffix: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              helperText="Example: include the word 'tracked' for $40+ tiers, and 'untracked' for <$40 tiers."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenTier(false); setEditTierId(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTier}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Policies Dialog */}
      <Dialog open={openPolicies} onClose={() => setOpenPolicies(false)} maxWidth="md" fullWidth>
        <DialogTitle>eBay Policies</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {POLICY_TYPES.map((t) => (
              <div key={t.value}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t.label}
                </Typography>
                <DataGrid
                  autoHeight
                  rows={policyOptions(t.value)}
                  getRowId={(r) => `${r.policy_type}-${r.policy_id}`}
                  columns={[
                    { field: "name", headerName: "Name", width: 380 },
                    { field: "policy_id", headerName: "Policy ID", width: 220 },
                    { field: "fetched_at", headerName: "Fetched", width: 180 },
                  ]}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 100]}
                  disableRowSelectionOnClick
                />
              </div>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPolicies(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
